import { useCallback, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  getDocument,
  GlobalWorkerOptions,
  RenderingCancelledException,
} from "pdfjs-dist";
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { useTranslationApp } from "../../../../lib/i18next";
import {
  LINKEDIN_DOCUMENT_PDF_PREVIEW_ALT,
  LINKEDIN_DOCUMENT_PDF_PREVIEW_ERROR,
  LINKEDIN_DOCUMENT_PDF_PREVIEW_LOADING,
  LINKEDIN_DOCUMENT_PREVIEW_NEXT,
  LINKEDIN_DOCUMENT_PREVIEW_OPEN_NEW_TAB,
  LINKEDIN_DOCUMENT_PREVIEW_PAGE_COUNT,
  LINKEDIN_DOCUMENT_PREVIEW_PREV,
} from "../../../../i18n/keysTranslations";

if (typeof window !== "undefined" && !GlobalWorkerOptions.workerSrc) {
  GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
}

const PREVIEW_STATUSES = {
  LOADING: "loading",
  READY: "ready",
  ERROR: "error",
};

const PREVIEW_MODES = {
  NONE: "none",
  CANVAS: "canvas",
  IFRAME: "iframe",
};

const OFFICE_EXTENSIONS = ["ppt", "pptx", "doc", "docx"];
const IFRAME_LOAD_TIMEOUT_MS = 20000;

const NAV_BUTTON_SX = {
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  width: 32,
  height: 32,
  backgroundColor: "rgba(255, 255, 255, 0.94) !important",
  boxShadow: "0 1px 6px rgba(0, 0, 0, 0.18)",
  zIndex: 2,
  "&:hover": {
    backgroundColor: "#fff !important",
  },
  "&.Mui-disabled": {
    opacity: 0.35,
  },
};

const isCancelledRenderError = (error) =>
  error instanceof RenderingCancelledException ||
  error?.name === "RenderingCancelledException";

const isHttpUrl = (url = "") => /^https?:\/\//i.test(url);

const isOfficeExtension = (extension = "") =>
  OFFICE_EXTENSIONS.includes(extension);

const buildOfficeEmbedUrl = (url) =>
  `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;

const buildPagedDocumentUrl = (url, page) => {
  const [withoutHash] = String(url).split("#");
  if (!page || page < 2) return withoutHash;
  return `${withoutHash}#page=${page}`;
};

const LinkedinDocumentPreview = ({ url, fileName = "", extension = "" }) => {
  const { t } = useTranslationApp();
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const iframeTimeoutRef = useRef(null);
  const pdfDocumentRef = useRef(null);
  const renderTaskRef = useRef(null);
  const currentPageRef = useRef(1);
  const [status, setStatus] = useState(PREVIEW_STATUSES.LOADING);
  const [mode, setMode] = useState(PREVIEW_MODES.NONE);
  const [iframeSrc, setIframeSrc] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(0);

  const normalizedExtension = String(extension || "").toLowerCase();
  const isPdf = normalizedExtension === "pdf";
  const isOffice = isOfficeExtension(normalizedExtension);

  const renderPage = useCallback(async (page) => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!page || !canvas || !container) return;

    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    if (containerWidth < 2 || containerHeight < 2) return;

    const unscaledViewport = page.getViewport({ scale: 1 });
    const fitScale = Math.min(
      containerWidth / unscaledViewport.width,
      containerHeight / unscaledViewport.height
    );
    const outputScale = Math.min(window.devicePixelRatio || 1, 2);
    const viewport = page.getViewport({
      scale: fitScale * outputScale,
    });

    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    canvas.style.width = `${Math.floor(viewport.width / outputScale)}px`;
    canvas.style.height = `${Math.floor(viewport.height / outputScale)}px`;

    if (renderTaskRef.current) {
      renderTaskRef.current.cancel();
    }

    renderTaskRef.current = page.render({
      canvas,
      viewport,
    });

    try {
      await renderTaskRef.current.promise;
    } catch (error) {
      if (isCancelledRenderError(error)) return;
      throw error;
    }
  }, []);

  const resetPreview = useCallback((nextStatus) => {
    pdfDocumentRef.current = null;
    setMode(PREVIEW_MODES.NONE);
    setIframeSrc("");
    setNumPages(0);
    setCurrentPage(1);
    setStatus(nextStatus);
  }, []);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    const isSupported = isPdf || (isOffice && isHttpUrl(url));
    if (!url || !isSupported) {
      resetPreview(PREVIEW_STATUSES.ERROR);
      return undefined;
    }

    let cancelled = false;
    let loadingTask = null;
    let pdfDocument = null;
    let resizeObserver = null;
    const clearIframeTimeout = () => {
      if (iframeTimeoutRef.current) {
        window.clearTimeout(iframeTimeoutRef.current);
        iframeTimeoutRef.current = null;
      }
    };

    const showIframePreview = (src) => {
      if (cancelled || !src) return;
      resetPreview(PREVIEW_STATUSES.LOADING);
      setMode(PREVIEW_MODES.IFRAME);
      setIframeSrc(src);
      clearIframeTimeout();
      iframeTimeoutRef.current = window.setTimeout(() => {
        if (!cancelled) {
          setStatus(PREVIEW_STATUSES.ERROR);
        }
      }, IFRAME_LOAD_TIMEOUT_MS);
    };

    const loadPdfPreview = async () => {
      resetPreview(PREVIEW_STATUSES.LOADING);

      try {
        let pdfData = null;

        try {
          const response = await fetch(url);
          if (response.ok) {
            pdfData = await response.arrayBuffer();
          }
        } catch {
          // CORS o red: el visor nativo no necesita lectura JS del archivo.
        }

        if (!pdfData) {
          if (isHttpUrl(url)) {
            showIframePreview(buildPagedDocumentUrl(url, 1));
            return;
          }
          setMode(PREVIEW_MODES.NONE);
          setStatus(PREVIEW_STATUSES.ERROR);
          return;
        }

        loadingTask = getDocument({
          data: pdfData,
          isEvalSupported: false,
        });
        pdfDocument = await loadingTask.promise;
        if (cancelled) return;

        pdfDocumentRef.current = pdfDocument;
        const totalPages = pdfDocument.numPages || 1;
        setNumPages(totalPages);
        setCurrentPage(1);
        currentPageRef.current = 1;
        setMode(PREVIEW_MODES.CANVAS);

        const page = await pdfDocument.getPage(1);
        if (cancelled) return;

        await renderPage(page);
        if (cancelled) return;

        setStatus(PREVIEW_STATUSES.READY);

        if (containerRef.current) {
          resizeObserver = new ResizeObserver(() => {
            const doc = pdfDocumentRef.current;
            if (!doc) return;
            doc
              .getPage(currentPageRef.current)
              .then((nextPage) => renderPage(nextPage))
              .catch(() => {});
          });
          resizeObserver.observe(containerRef.current);
        }
      } catch (error) {
        if (cancelled || isCancelledRenderError(error)) return;
        if (isHttpUrl(url)) {
          showIframePreview(buildPagedDocumentUrl(url, 1));
          return;
        }
        setMode(PREVIEW_MODES.NONE);
        setStatus(PREVIEW_STATUSES.ERROR);
      }
    };

    if (isPdf) {
      loadPdfPreview();
    } else {
      showIframePreview(buildOfficeEmbedUrl(url));
    }

    return () => {
      cancelled = true;
      clearIframeTimeout();
      resizeObserver?.disconnect();
      renderTaskRef.current?.cancel();
      loadingTask?.destroy?.().catch(() => {});
      pdfDocument?.destroy?.();
      pdfDocumentRef.current = null;
    };
  }, [url, isOffice, isPdf, renderPage, resetPreview]);

  useEffect(() => {
    const doc = pdfDocumentRef.current;
    if (!doc || mode !== PREVIEW_MODES.CANVAS) return undefined;
    if (status !== PREVIEW_STATUSES.READY && currentPage === 1) {
      return undefined;
    }

    let cancelled = false;

    const renderCurrentPage = async () => {
      try {
        const page = await doc.getPage(currentPage);
        if (cancelled) return;
        await renderPage(page);
      } catch (error) {
        if (cancelled || isCancelledRenderError(error)) return;
        setStatus(PREVIEW_STATUSES.ERROR);
      }
    };

    renderCurrentPage();

    return () => {
      cancelled = true;
    };
  }, [currentPage, mode, renderPage, status]);

  const isReady = status === PREVIEW_STATUSES.READY;
  const isError = status === PREVIEW_STATUSES.ERROR;
  const showIframe = mode === PREVIEW_MODES.IFRAME && Boolean(iframeSrc);
  const showCanvas = mode === PREVIEW_MODES.CANVAS;
  const canNavigatePages = showCanvas && isReady && numPages > 1;
  const canOpenInNewTab = Boolean(url) && isReady && !isError;
  const previewLabel = t(LINKEDIN_DOCUMENT_PDF_PREVIEW_ALT, {
    fileName,
  });

  const onIframeLoad = () => {
    if (iframeTimeoutRef.current) {
      window.clearTimeout(iframeTimeoutRef.current);
      iframeTimeoutRef.current = null;
    }
    setStatus(PREVIEW_STATUSES.READY);
  };

  const onIframeError = () => {
    setStatus(PREVIEW_STATUSES.ERROR);
  };

  const onGoToPreviousPage = () => {
    setCurrentPage((page) => Math.max(1, page - 1));
  };

  const onGoToNextPage = () => {
    setCurrentPage((page) => Math.min(numPages, page + 1));
  };

  const onOpenInNewTab = () => {
    if (!url) return;
    const nextUrl = isPdf ? buildPagedDocumentUrl(url, currentPage) : url;
    window.open(nextUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        backgroundColor: "#EEF0F2",
      }}
    >
      {!isReady && !isError && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            px: 2,
            backgroundColor: "#EEF0F2",
          }}
        >
          <CircularProgress size={28} sx={{ color: "#000" }} />
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", textAlign: "center" }}
          >
            {t(LINKEDIN_DOCUMENT_PDF_PREVIEW_LOADING)}
          </Typography>
        </Box>
      )}

      {isError && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            px: 2,
            textAlign: "center",
          }}
        >
          <InsertDriveFileOutlinedIcon
            sx={{ fontSize: 36, color: "text.secondary" }}
          />
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {t(LINKEDIN_DOCUMENT_PDF_PREVIEW_ERROR)}
          </Typography>
        </Box>
      )}

      <canvas
        ref={canvasRef}
        aria-label={previewLabel}
        style={{
          display: showCanvas && isReady ? "block" : "none",
          maxWidth: "100%",
          maxHeight: "100%",
          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
          backgroundColor: "#fff",
        }}
      />

      {showIframe && !isError && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            overflow: "hidden",
            backgroundColor: "#fff",
            opacity: isReady ? 1 : 0,
          }}
        >
          <iframe
            title={previewLabel}
            src={iframeSrc}
            onLoad={onIframeLoad}
            onError={onIframeError}
            style={{
              position: "absolute",
              top: isOffice ? -52 : 0,
              left: 0,
              width: "100%",
              height: isOffice ? "calc(100% + 124px)" : "100%",
              border: 0,
              pointerEvents: "none",
            }}
          />
        </Box>
      )}

      {canNavigatePages && (
        <>
          <IconButton
            type="button"
            aria-label={t(LINKEDIN_DOCUMENT_PREVIEW_PREV)}
            onClick={onGoToPreviousPage}
            disabled={currentPage <= 1}
            sx={{ ...NAV_BUTTON_SX, left: 8 }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <IconButton
            type="button"
            aria-label={t(LINKEDIN_DOCUMENT_PREVIEW_NEXT)}
            onClick={onGoToNextPage}
            disabled={currentPage >= numPages}
            sx={{ ...NAV_BUTTON_SX, right: 8 }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </>
      )}

      {(canNavigatePages || canOpenInNewTab) && (
        <Box
          sx={{
            position: "absolute",
            left: 8,
            right: 8,
            bottom: 8,
            zIndex: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          {canNavigatePages && (
            <Box
              sx={{
                px: 1.25,
                py: 0.35,
                borderRadius: 5,
                backgroundColor: "rgba(0, 0, 0, 0.62)",
                color: "#fff",
                fontSize: "0.75rem",
                fontWeight: 600,
                letterSpacing: 0.2,
                lineHeight: 1.4,
              }}
            >
              {t(LINKEDIN_DOCUMENT_PREVIEW_PAGE_COUNT, {
                current: currentPage,
                total: numPages,
              })}
            </Box>
          )}
          {canOpenInNewTab && (
            <IconButton
              type="button"
              aria-label={t(LINKEDIN_DOCUMENT_PREVIEW_OPEN_NEW_TAB)}
              onClick={onOpenInNewTab}
              sx={{
                position: "absolute",
                right: 0,
                pointerEvents: "auto",
                width: 32,
                height: 32,
                backgroundColor: "rgba(255, 255, 255, 0.94) !important",
                boxShadow: "0 1px 6px rgba(0, 0, 0, 0.18)",
                "&:hover": {
                  backgroundColor: "#fff !important",
                },
              }}
            >
              <OpenInNewIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Box>
      )}
    </Box>
  );
};

export default LinkedinDocumentPreview;
